import React, { useState, useEffect } from 'react';
import {
    Card,
    Tabs,
    Form,
    Input,
    Button,
    Space,
    message,
    Popconfirm,
    Typography,
    Divider,
    Row,
    Col,
    List,
} from 'antd';
import { PlusOutlined, DeleteOutlined, MenuOutlined } from '@ant-design/icons';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { optionService } from '../services/option';
import CommissionSettings from '../components/CommissionSettings';
import OrderStatusOptionsSettings from '../components/OrderStatusOptionsSettings';
import ListHelpSettings from '../components/ListHelpSettings';
import useDocumentTitle from '../hooks/useDocumentTitle';

const { Title, Text } = Typography;

interface DataSettingsProps {
    optionKey: string;
    title: string;
    description?: string;
}

interface SortableItemProps {
    id: string;
    item: string;
    index: number;
    onDelete: (index: number) => void;
    onMoveUp: (index: number) => void;
    onMoveDown: (index: number) => void;
    isFirst: boolean;
    isLast: boolean;
}

const SortableItem: React.FC<SortableItemProps> = ({
                                                       id,
                                                       item,
                                                       index,
                                                       onDelete,
                                                       onMoveUp,
                                                       onMoveDown,
                                                       isFirst,
                                                       isLast
                                                   }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <List.Item
            ref={setNodeRef}
            style={style}
            actions={[
                <Button
                    type="text"
                    size="small"
                    disabled={isFirst}
                    onClick={() => onMoveUp(index)}
                    title="上移"
                >
                    ↑
                </Button>,
                <Button
                    type="text"
                    size="small"
                    disabled={isLast}
                    onClick={() => onMoveDown(index)}
                    title="下移"
                >
                    ↓
                </Button>,
                <Popconfirm
                    title="确定删除此项吗？"
                    onConfirm={() => onDelete(index)}
                    okText="确定"
                    cancelText="取消"
                >
                    <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        title="删除"
                    />
                </Popconfirm>
            ]}
        >
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <div
                    {...attributes}
                    {...listeners}
                    style={{
                        cursor: isDragging ? 'grabbing' : 'grab',
                        marginRight: 8,
                        padding: '4px',
                        color: '#999'
                    }}
                >
                    <MenuOutlined />
                </div>
                <Text>{item}</Text>
            </div>
        </List.Item>
    );
};

const DataSettingsForm: React.FC<DataSettingsProps> = ({ optionKey, title, description }) => {
    const [form] = Form.useForm();
    const [items, setItems] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        loadItems();
    }, [optionKey]);

    const loadItems = async () => {
        try {
            setLoading(true);
            const data = await optionService.get(optionKey);
            setItems(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Failed to load items:', error);
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    const saveItems = async (newItems: string[]) => {
        try {
            setLoading(true);
            await optionService.set(optionKey, newItems);
            setItems(newItems);
            message.success('保存成功');
        } catch (error) {
            message.error('保存失败');
            console.error('Failed to save items:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        if (!inputValue.trim()) {
            message.warning('请输入内容');
            return;
        }

        if (items.includes(inputValue.trim())) {
            message.warning('该项已存在');
            return;
        }

        const newItems = [...items, inputValue.trim()];
        saveItems(newItems);
        setInputValue('');
    };

    const handleDelete = (index: number) => {
        const newItems = items.filter((_, i) => i !== index);
        saveItems(newItems);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            const oldIndex = items.indexOf(active.id as string);
            const newIndex = items.indexOf(over?.id as string);

            const newItems = arrayMove(items, oldIndex, newIndex);
            saveItems(newItems);
        }
    };

    const moveItem = (fromIndex: number, toIndex: number) => {
        const newItems = [...items];
        const [movedItem] = newItems.splice(fromIndex, 1);
        newItems.splice(toIndex, 0, movedItem);
        saveItems(newItems);
    };

    const moveUp = (index: number) => {
        if (index > 0) {
            moveItem(index, index - 1);
        }
    };

    const moveDown = (index: number) => {
        if (index < items.length - 1) {
            moveItem(index, index + 1);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAdd();
        }
    };

    return (
        <div>
            {title && <Title level={4}>{title}</Title>}
            {description && <Text type="secondary">{description}</Text>}
            {(title || description) && <Divider />}

            <Row gutter={16}>
                <Col span={10}>
                    <Card title="添加新项" size="small">
                        <Space.Compact style={{ width: '100%' }}>
                            <Input
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="请输入内容后按回车或点击添加"
                                disabled={loading}
                                data-testid={`ds-input-${optionKey}`}
                            />
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleAdd}
                                loading={loading}
                                data-testid={`ds-btn-add-${optionKey}`}
                            >
                                添加
                            </Button>
                        </Space.Compact>
                    </Card>
                </Col>

                <Col span={14}>
                    <Card title={`当前项列表 (${items.length})`} size="small">
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext items={items} strategy={verticalListSortingStrategy}>
                                <List
                                    dataSource={items}
                                    locale={{ emptyText: '暂无数据' }}
                                    size="small"
                                    data-testid={`ds-list-${optionKey}`}
                                    renderItem={(item, index) => (
                                        <SortableItem
                                            key={item}
                                            id={item}
                                            item={item}
                                            index={index}
                                            onDelete={handleDelete}
                                            onMoveUp={moveUp}
                                            onMoveDown={moveDown}
                                            isFirst={index === 0}
                                            isLast={index === items.length - 1}
                                        />
                                    )}
                                />
                            </SortableContext>
                        </DndContext>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

const DataSettings: React.FC = () => {
    useDocumentTitle('数据设置 - 设置');
    const tabItems = [
        // {
        //     key: 'trademark_types',
        //     label: '商标类型',
        //     children: (
        //         <DataSettingsForm
        //             optionKey="trademark_types"
        //             title=""
        //             description=""
        //         />
        //     ),
        // },
        {
            key: 'customer_types',
            label: '客户等级',
            children: (
                <DataSettingsForm
                    optionKey="customer_types"
                    title=""
                    description=""
                />
            ),
        },
        {
            key: 'customer_sources',
            label: '客户来源',
            children: (
                <DataSettingsForm
                    optionKey="customer_sources"
                    title=""
                    description=""
                />
            ),
        },
        {
            key: 'commission_rates',
            label: '提成比例',
            children: <CommissionSettings />,
        },
        // {
        //     key: 'order_statuses',
        //     label: '订单状态',
        //     children: (
        //         <DataSettingsForm
        //             optionKey="order_statuses"
        //             title=""
        //             description=""
        //         />
        //     ),
        // },
        // {
        //     key: 'service_categories',
        //     label: '服务类别',
        //     children: (
        //         <DataSettingsForm
        //             optionKey="service_categories"
        //             title=""
        //             description=""
        //         />
        //     ),
        // },
        {
            key: 'notification_emails',
            label: '通知邮箱',
            children: (
                <DataSettingsForm
                    optionKey="notification_emails"
                    title=""
                    description="配置系统通知邮箱地址，用于发送订单状态、商标更新等通知邮件"
                />
            ),
        },
        {
            key: 'list_help_settings',
            label: '常见问题',
            children: <ListHelpSettings />,
        },
        {
            key: 'order_status_options',
            label: '订单状态选项',
            children: <OrderStatusOptionsSettings />,
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <Card>
                <Tabs
                    items={tabItems}
                    defaultActiveKey="customer_types"
                    size="large"
                />
            </Card>
        </div>
    );
};

export default DataSettings;
